import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Info, Lock } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ClientActions } from '@/components/consultant/ClientActions';
import { ClientFieldsCard } from '@/components/consultant/ClientFieldsCard';
import {
  FarmSetupDialog,
  type DepartmentOption,
  type FarmSetupResult,
} from '@/components/consultant/FarmSetupDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityForm } from '@/components/crm/ActivityForm';
import { ContactForm } from '@/components/crm/ContactForm';
import {
  ActivityStatusBadge,
  ActivityTypeBadge,
} from '@/components/crm/ActivityBadge';
import { router } from '@/router';
import { api, ApiError } from '@/lib/api';
import { getCompany, listActivities, listContacts } from '@/lib/crm/api';
import { getClientFieldsSummary } from '@/lib/consultant/api';
import { useClientFlow } from '@/lib/consultant/useClientFlow';
import type { ClientFieldsSummary, ClientState } from '@/lib/consultant/types';
import { formatDateTime, initials } from '@/lib/crm/format';
import type {
  CrmActivity,
  CrmCompany,
  CrmContact,
} from '@/lib/crm/types';

interface CrmDetailPageProps {
  companyId: string;
}

export function CrmDetailPage({ companyId }: CrmDetailPageProps) {
  const [company, setCompany] = useState<CrmCompany | null>(null);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [activities, setActivities] = useState<CrmActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [contactOpen, setContactOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  const [summary, setSummary] = useState<ClientFieldsSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [setupOpen, setSetupOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [co, cts, acts] = await Promise.all([
        getCompany(companyId),
        listContacts(companyId),
        listActivities(companyId),
      ]);
      setCompany(co);
      setContacts(cts);
      setActivities(acts);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setError('Клиент не найден');
      } else {
        setError(e instanceof ApiError ? e.detail : 'Не удалось загрузить клиента');
      }
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const flow = useClientFlow(() => void fetchAll());

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  // Сводка полей — здесь, а не внутри блока «Поля клиента»: по ней же
  // решается, доступно ли «Показать на карте». Два запроса за одним числом —
  // это два разных ответа на один вопрос, расходящихся во времени.
  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    getClientFieldsSummary(companyId)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<DepartmentOption[]>('/api/v1/clusters')
      .then((rows) => {
        if (!cancelled && Array.isArray(rows)) setDepartments(rows);
      })
      .catch(() => {
        if (!cancelled) setDepartments([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fieldsCount = summary?.fields_count ?? 0;
  // Состояние карточки — теми же тремя значениями, что и строка списка:
  // одинаковые действия обязаны читать одинаковое состояние, иначе список и
  // карточка будут гасить разные кнопки у одного и того же клиента.
  const clientState: ClientState =
    fieldsCount > 0
      ? 'in_system'
      : company?.farm_name || company?.linked_tenant_id
        ? 'farm_ready'
        : 'card_only';
  const isLead = clientState !== 'in_system';

  const flowClient = { external_id: company?.external_id ?? null, crm_company_id: companyId };

  const handleFarmSetup = async (result: FarmSetupResult) => {
    const ok = await flow.applyFarmSetup(flowClient, result);
    if (ok && !result.addFields) setSetupOpen(false);
  };

  if (loading) {
    return (
      <AppShell>
        <div className="mx-auto w-full max-w-5xl space-y-4 p-6" data-testid="detail-loading">
          <Skeleton className="h-8 w-64" />
          <div className="grid gap-4 md:grid-cols-[380px_1fr]">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </AppShell>
    );
  }

  if (error || !company) {
    return (
      <AppShell>
        <div className="mx-auto w-full max-w-5xl space-y-4 p-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => router.navigate({ to: '/crm' })}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Клиенты
          </Button>
          <Alert variant="destructive">
            <AlertDescription>{error ?? 'Клиент не найден'}</AlertDescription>
          </Alert>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-5xl space-y-5 p-6 pb-fab">
        {/* header */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => router.navigate({ to: '/crm' })}
            data-testid="back-to-list"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Клиенты
          </Button>
          <h1 className="text-2xl font-semibold">{company.name}</h1>
          {clientState === 'in_system' ? (
            <Badge variant="primary" data-testid="tenant-badge">
              полей: {fieldsCount}
            </Badge>
          ) : clientState === 'farm_ready' ? (
            <Badge variant="accent-brand" data-testid="farm-ready-badge">
              заведён · полей нет
            </Badge>
          ) : (
            <Badge variant="outline" data-testid="lead-badge">
              нет в системе
            </Badge>
          )}
        </div>

        {/* Те же три действия и в том же порядке, что в строке списка. Единый
            компонент, а не «похожие кнопки»: разъехавшись, два места начинают
            гасить разное у одного клиента. */}
        <ClientActions
          state={clientState}
          fieldsCount={fieldsCount}
          clusterName={null}
          busy={flow.busy}
          onSetupFarm={() => setSetupOpen(true)}
          onAddFields={() => {
            if (clientState === 'card_only') setSetupOpen(true);
            else void flow.goAddFields(flowClient);
          }}
          onShowOnMap={() => void flow.goToMap(flowClient)}
        />

        {flow.error && (
          <Alert variant="destructive" data-testid="client-action-error">
            <AlertDescription>{flow.error}</AlertDescription>
          </Alert>
        )}

        {/* Клиент без полей — НЕ «недозаведённый», а штатное состояние
            (модель D). Поэтому объяснение, а не призыв нажать ещё одну
            кнопку: кабинет заведётся сам вместе с первым полем. */}
        {isLead && (
          <Alert data-testid="lead-notice">
            <Info className="h-4 w-4" />
            <AlertTitle>У клиента пока нет полей</AlertTitle>
            <AlertDescription>
              Журнал, контакты и активности доступны уже сейчас. Рабочий кабинет
              клиента — карта, осмотры, снимки — создастся сам, когда вы добавите
              первое поле.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 md:grid-cols-[380px_1fr]">
          {/* left: requisites + tags + contacts */}
          <div className="space-y-4">
            <Card className="space-y-3 p-4">
              <p className="text-sm font-semibold">Реквизиты</p>
              <dl className="space-y-1.5 text-sm">
                <Row label="ИНН" value={company.inn} />
                <Row label="ОГРН" value={company.ogrn} />
                <Row label="Адрес" value={company.address} />
                <Row label="Директор" value={company.director_name} />
                <Row label="Телефон" value={company.director_phone} />
                <Row label="Email" value={company.director_email} />
              </dl>
              <Separator />
              <p className="text-sm font-semibold">Теги</p>
              <div className="flex flex-wrap gap-1">
                {company.tags.length === 0 ? (
                  <span className="text-xs text-muted-foreground">
                    Теги не заданы
                  </span>
                ) : (
                  company.tags.map((t) => (
                    <Badge key={t} variant="secondary">
                      {t}
                    </Badge>
                  ))
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Теги свободные (без преднабора).
              </p>
            </Card>

            <Card className="space-y-3 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Контакты</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setContactOpen(true)}
                  data-testid="add-contact"
                >
                  + Контакт
                </Button>
              </div>
              {contacts.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="contacts-empty">
                  Контакты не добавлены.
                </p>
              ) : (
                <div className="space-y-2" data-testid="contacts-list">
                  {contacts.map((ct) => (
                    <div key={ct.id} className="flex items-start gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {initials(ct.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 font-medium">
                          {ct.full_name}
                          {ct.is_primary && (
                            <Badge variant="primary" className="text-[10px]">
                              основной
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {[ct.position, ct.phone, ct.email]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Поля клиента — сводка до входа в его окно (UC-1 пп. 4–5).
                Показывается ВСЕГДА, в том числе клиенту без кабинета: именно
                отсюда идёт «Добавить поле», которое кабинет и создаёт. */}
            {company && (
              <ClientFieldsCard
                clientName={company.name}
                summary={summary}
                loading={summaryLoading}
              />
            )}

            {/* lead: scouting locked block (screen 3) */}
            {isLead && (
              <Card className="space-y-2 p-4">
                <p className="text-sm font-semibold">Поля и осмотры</p>
                <div className="flex items-center justify-center rounded-md border border-border bg-muted/40 py-6">
                  <span className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" />
                    Осмотр недоступен — нет окна клиента
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Осмотр поля выполняется только с карты в окне клиента. Добавьте
                  клиенту первое поле — окно откроется вместе с ним.
                </p>
              </Card>
            )}
          </div>

          {/* right: activity timeline */}
          <Card className="space-y-4 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">История активностей</p>
              <Button
                size="sm"
                onClick={() => setActivityOpen(true)}
                data-testid="add-activity"
              >
                + Активность
              </Button>
            </div>
            {activities.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="activities-empty">
                Пока нет активностей. Зафиксируйте первое касание клиента.
              </p>
            ) : (
              <ol className="space-y-3" data-testid="activity-timeline">
                {activities.map((a) => {
                  const completed = !!a.completed_at;
                  return (
                    <li
                      key={a.id}
                      className="rounded-lg border border-border p-3"
                      data-testid={`activity-${a.id}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <ActivityTypeBadge type={a.type} />
                        <ActivityStatusBadge completed={completed} />
                        <span className="ml-auto text-xs text-muted-foreground">
                          {completed
                            ? formatDateTime(a.completed_at)
                            : `план: ${formatDateTime(a.planned_at)}`}
                        </span>
                      </div>
                      {a.notes && (
                        <p className="mt-2 text-sm">{a.notes}</p>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
            {isLead && (
              <p className="text-xs text-muted-foreground">
                Журнал лида работает без тенанта. Только осмотр полей требует
                завести клиента.
              </p>
            )}
          </Card>
        </div>
      </div>

      <ContactForm
        open={contactOpen}
        onOpenChange={setContactOpen}
        companyId={company.id}
        onCreated={(ct) =>
          setContacts((prev) =>
            [ct, ...prev].sort(
              (a, b) => Number(b.is_primary) - Number(a.is_primary),
            ),
          )
        }
      />
      <ActivityForm
        open={activityOpen}
        onOpenChange={setActivityOpen}
        companyId={company.id}
        contacts={contacts}
        onCreated={(a) => setActivities((prev) => [a, ...prev])}
      />
      {company && (
        <FarmSetupDialog
          open={setupOpen}
          onOpenChange={setSetupOpen}
          client={{
            title: company.name,
            external_id: company.external_id,
            inn: company.inn,
            city: company.address,
            farm_name: company.farm_name,
            cluster_id: company.cluster_id,
          }}
          departments={departments}
          submitting={flow.busy}
          error={flow.error}
          onSubmit={(result) => void handleFarmSetup(result)}
        />
      )}
    </AppShell>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value || <span className="text-muted-foreground">—</span>}</dd>
    </div>
  );
}
